import { tokens } from '@sparkpost/design-tokens';
import { CheckCircle, Error as ErrorIcon } from '@sparkpost/matchbox-icons';
import React, { useCallback, useEffect, useState } from 'react';
import { CopyField } from 'src/components';
import {
  Box,
  Button,
  Checkbox,
  Modal,
  Progress,
  Spinner,
  Stack,
  Text
} from 'src/components/matchbox';
import { SubduedText } from 'src/components/text';
import { MIGRATION_STEPS, STEP_STATUS } from '../constants/tlsMigration';
import useDomains from '../hooks/useDomains';

const STEP_LABELS = ['Check', 'Update DNS', 'Enable'];

export default function TLSMigrationModal({ domain, isOpen, onClose, cnameV2, sourceType }) {
  const [currentStep, setCurrentStep] = useState(MIGRATION_STEPS.ENABLE);
  const [stepStatus, setStepStatus] = useState(STEP_STATUS.IDLE);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [dnsConfirmed, setDnsConfirmed] = useState(false);
  const [isPermanentFailure, setIsPermanentFailure] = useState(false);

  const {
    checkTrackingDomainCertificate,
    enableTrackingDomainCertificate,
    verifyTrackingDomain,
    listTrackingDomains
  } = useDomains();

  const domainName = domain?.domain;
  const subaccountId = domain?.subaccount_id;
  const isVerified = domain?.status?.verified;

  const modalTitle =
    sourceType === 'http-only'
      ? 'Enable SparkPost Managed HTTPS'
      : 'Switch to SparkPost Managed HTTPS';

  // Allow closing when not in loading state
  const canClose = stepStatus !== STEP_STATUS.LOADING;

  // Handle close
  const handleClose = useCallback(() => {
    if (canClose) {
      // Refresh domain data if changes were made
      if (hasChanges) {
        listTrackingDomains();
      }
      onClose();
    }
  }, [canClose, hasChanges, listTrackingDomains, onClose]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(MIGRATION_STEPS.ENABLE);
      setStepStatus(STEP_STATUS.IDLE);
      setErrorMessage('');
      setHasChanges(false);
      setIsPermanentFailure(false);
      setDnsConfirmed(false);
    }
  }, [isOpen]);

  // Extract error message from API response
  const getApiErrorMessage = (error, fallback = 'The request could not be fulfilled.') => {
    return error.response?.data?.errors?.[0]?.message || error.message || fallback;
  };

  // Step 1: Check certificate eligibility
  const handleCheck = useCallback(async () => {
    setStepStatus(STEP_STATUS.LOADING);
    try {
      await checkTrackingDomainCertificate({ domain: domainName, subaccountId });
      setCurrentStep(MIGRATION_STEPS.DNS);
      setStepStatus(STEP_STATUS.IDLE);
    } catch (error) {
      setStepStatus(STEP_STATUS.ERROR);
      if (error.response?.status === 403) {
        setIsPermanentFailure(true);
      }
      setErrorMessage(getApiErrorMessage(error));
    }
  }, [domainName, subaccountId, checkTrackingDomainCertificate]);

  // Step 2: Verify (if unverified) and enable managed certificate
  const handleEnable = useCallback(async () => {
    setStepStatus(STEP_STATUS.LOADING);
    try {
      // If domain is unverified, verify first to ensure it points to v2 endpoint
      if (!isVerified) {
        await verifyTrackingDomain({ domain: domainName, subaccountId, silent: true });
        setHasChanges(true);
      }
      await enableTrackingDomainCertificate({ domain: domainName, subaccountId });
      setHasChanges(true);
      setCurrentStep(MIGRATION_STEPS.ISSUANCE);
      setStepStatus(STEP_STATUS.SUCCESS);
    } catch (error) {
      setStepStatus(STEP_STATUS.ERROR);
      if (error.response?.status === 403) {
        setIsPermanentFailure(true);
      }
      setErrorMessage(getApiErrorMessage(error));
    }
  }, [domainName, subaccountId, isVerified, verifyTrackingDomain, enableTrackingDomainCertificate]);

  const handleDnsConfirmedChange = useCallback((e) => {
    setDnsConfirmed(e.target.checked);
  }, []);

  const renderProgress = () => (
    <Box minWidth="180px" paddingRight="500">
      <Progress orientation="vertical">
        <Progress.Step
          completed={currentStep > MIGRATION_STEPS.ENABLE}
          selected={currentStep === MIGRATION_STEPS.ENABLE}
        >
          {STEP_LABELS[0]}
        </Progress.Step>
        <Progress.Step
          completed={currentStep > MIGRATION_STEPS.DNS}
          selected={currentStep === MIGRATION_STEPS.DNS}
        >
          {STEP_LABELS[1]}
        </Progress.Step>
        <Progress.Step
          completed={stepStatus === STEP_STATUS.SUCCESS && currentStep === MIGRATION_STEPS.ISSUANCE}
          selected={currentStep === MIGRATION_STEPS.ISSUANCE}
        >
          {STEP_LABELS[2]}
        </Progress.Step>
      </Progress>
    </Box>
  );

  // Step 1: Check
  const renderStep1 = () => {
    if (stepStatus === STEP_STATUS.ERROR) {
      return (
        <Stack>
          <Box display="flex" flexDirection="column" alignItems="center" padding="600">
            <ErrorIcon size={48} color={tokens.color_red_700} />
            <Text marginTop="400" fontWeight="semibold">
              {errorMessage}
            </Text>
            {isPermanentFailure && (
              <SubduedText textAlign="center" marginTop="200">
                You can still use HTTPS by configuring your own CDN or proxy server for TLS
                termination.
              </SubduedText>
            )}
            {!isPermanentFailure && (
              <SubduedText textAlign="center" marginTop="200">
                Please retry again later.
              </SubduedText>
            )}
          </Box>
        </Stack>
      );
    }

    if (stepStatus === STEP_STATUS.LOADING) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" padding="600">
          <Spinner size="large" />
          <Text marginTop="400">Checking support...</Text>
          <SubduedText>Verifying if domain supports managed HTTPS</SubduedText>
        </Box>
      );
    }

    return (
      <Stack>
        <Text>
          SparkPost is able to automatically provision, host and renew a TLS certificate for your
          domain, enabling HTTPS for click and open tracking. This feature uses Let's Encrypt, a
          well known and widely used Certificate Authority to issue valid certificates.
        </Text>

        <Text>
          Before continuing, we&apos;ll check that Let&apos;s Encrypt can issue a certificate for
          your domain. You will then be required to update the CNAME record of your domain.
        </Text>

        <Text>Tracking domain:</Text>
        <Box
          backgroundColor="gray.100"
          padding="400"
          borderRadius="200"
          fontFamily="monospace"
          fontSize="300"
        >
          {domainName}
        </Box>
      </Stack>
    );
  };

  // Step 2: DNS
  const renderStep2 = () => {
    if (stepStatus === STEP_STATUS.ERROR) {
      return (
        <Stack>
          <Box display="flex" flexDirection="column" alignItems="center" padding="600">
            <ErrorIcon size={48} color={tokens.color_red_700} />
            <Text marginTop="400" fontWeight="semibold" textAlign="center">
              {errorMessage}
            </Text>
            {isPermanentFailure && (
              <SubduedText textAlign="center" marginTop="200">
                You can still use HTTPS by configuring your own CDN or proxy server for TLS
                termination.
              </SubduedText>
            )}
          </Box>
        </Stack>
      );
    }

    if (stepStatus === STEP_STATUS.LOADING) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" padding="600">
          <Spinner size="large" />
          <Text marginTop="400">Enabling managed HTTPS...</Text>
          <SubduedText>This may take a moment</SubduedText>
        </Box>
      );
    }

    return (
      <Stack>
        <Text>
          Add or update the CNAME record at your DNS provider. Once completed, click the{' '}
          <i>Confirm</i> button below.
          <br></br>
          <br></br>
          This is a <strong>required step</strong> for enabling managed certificates for your
          domain.
        </Text>

        <Box marginTop="400">
          <Text fontWeight="semibold" marginBottom="200">
            CNAME Record
          </Text>
          <Stack space="200">
            <CopyField id="cname-hostname" label="Hostname" value={domainName} />
            <CopyField id="cname-value" label="New Value" value={cnameV2} />
          </Stack>
        </Box>

        <Checkbox
          id="dns-confirmed"
          label="I've updated this DNS record"
          checked={dnsConfirmed}
          onChange={handleDnsConfirmedChange}
        />
      </Stack>
    );
  };

  // Step 3: Enable Success
  const renderStep3 = () => {
    return (
      <Stack>
        <Box display="flex" flexDirection="column" alignItems="center" padding="600">
          <CheckCircle size={48} color={tokens.color_green_700} />
          <Text marginTop="400" fontWeight="semibold">
            Managed HTTPS enabled successfully
          </Text>
          <SubduedText textAlign="center">
            Certificate issuance is now in progress. Your tracking domain will be secured with HTTPS
            shortly.
          </SubduedText>
        </Box>
      </Stack>
    );
  };

  // Render content based on current step
  const renderContent = () => {
    switch (currentStep) {
      case MIGRATION_STEPS.ENABLE:
        return renderStep1();
      case MIGRATION_STEPS.DNS:
        return renderStep2();
      case MIGRATION_STEPS.ISSUANCE:
        return renderStep3();
      default:
        return null;
    }
  };

  // Render footer buttons
  const renderFooter = () => {
    // Step 1: Check
    if (currentStep === MIGRATION_STEPS.ENABLE) {
      if (stepStatus === STEP_STATUS.ERROR) {
        return (
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            {!isPermanentFailure && (
              <Button variant="primary" onClick={handleCheck}>
                Retry
              </Button>
            )}
          </Modal.Footer>
        );
      }
      return (
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCheck}>
            Continue
          </Button>
        </Modal.Footer>
      );
    }

    // Step 2: DNS + Enable
    if (currentStep === MIGRATION_STEPS.DNS) {
      if (stepStatus === STEP_STATUS.ERROR) {
        return (
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            {!isPermanentFailure && (
              <Button variant="primary" onClick={handleEnable}>
                Retry
              </Button>
            )}
          </Modal.Footer>
        );
      }
      return (
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEnable} disabled={!dnsConfirmed}>
            Confirm
          </Button>
        </Modal.Footer>
      );
    }

    // Step 3: Success
    if (currentStep === MIGRATION_STEPS.ISSUANCE) {
      return (
        <Modal.Footer>
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        </Modal.Footer>
      );
    }

    return null;
  };

  return (
    <Modal open={isOpen} onClose={canClose ? handleClose : undefined}>
      <Modal.Header showCloseButton={canClose}>{modalTitle}</Modal.Header>
      <Modal.Content paddingTop={60} paddingBottom={30}>
        <Box display="flex">
          {!isPermanentFailure && renderProgress()}
          <Box flex="1">{renderContent()}</Box>
        </Box>
      </Modal.Content>

      {stepStatus !== STEP_STATUS.LOADING && renderFooter()}
    </Modal>
  );
}
